// @flow

/**
 * Masonry as a whole builds layouts using multi-stage rendering, first to
 * measure items, and then to build and render a completed layout.
 * LayoutRenderer is the coordinator of these renders. It is responsible for
 * calling out to layout engines to build layouts, merging item sizes into
 * layouts once they've been measured, and virtualizing layouts based on the
 * scroll viewport.
 */

import * as React from 'react';
import isEqual from 'lodash/isEqual';
import Virtualizer from 'app/common/react/components/Masonry9/Virtualizer';
import { type LayoutEngine } from 'app/common/react/components/Masonry9/LayoutEngine';
import Collection, { type renderItemProps } from 'app/common/react/components/Masonry9/Collection';
import Layout, {
  type Size,
  type Position,
  type ItemLayoutInfo,
} from 'app/common/react/components/Masonry9/Layout';

export type getVirtualOverscanProps<ItemType> = {
  layout: Layout<ItemType>,
  scrollContainerSize: Size,
  contentContainerSize: Size,
};
export type getItemsToMeasureLimitProps<ItemType> = {
  layout: Layout<ItemType>,
  scrollContainerSize: ?Size,
  contentContainerSize: Size,
};

type Props<ItemType> = {|
  // Managed props -- changing these will cause LayoutRenderer to re-layout:
  items: Array<ItemType>,
  scrollContainerSize?: ?Size,
  scrollPosition?: ?Position,
  contentContainerSize: Size,

  // Unmanaged props -- use keys to reset LayoutRenderer if you need to change
  // any of these props after initial render:
  getItemLayoutOptions?: (item: ItemType) => ?Object,
  initialLayout?: Layout<ItemType>,
  layoutEngine: LayoutEngine<ItemType>,
  onLayoutUpdated?: (layout: Layout<ItemType>) => void,
  renderItem: (props: renderItemProps<ItemType>) => React.Node,
  virtualize: boolean,
  virtualOverscan?: number | ((props: getVirtualOverscanProps<ItemType>) => number),
  itemsToMeasureLimit?: number | ((props: getItemsToMeasureLimitProps<ItemType>) => number),
|};

type State<ItemType> = {|
  layout: Layout<ItemType>,

  // Copied from props:
  items: Array<ItemType>,
  scrollContainerSize?: ?Size,
  scrollPosition?: ?Position,
  contentContainerSize: Size,
|};

// Define a simple method that filters out null values. This needed to be pulled
// out of the class definition to fix flow typing with generics.
const removeNulls = <ItemType>(items: Array<ItemType>): Array<ItemType> =>
  items.filter((item) => !!item);

// Users of LayoutRenderer can pass a number or function to determine
// virtualizer overscan, but this acts as a good default for most.
const defaultGetVirtualOverscan = <ItemType>({
  scrollContainerSize,
}: getVirtualOverscanProps<ItemType>) =>
  Math.max(scrollContainerSize.width, scrollContainerSize.height) * 2;

// Create a comparitor for arrays. Users may pass cloned items arrays into
// Masonry and we don't want to have to rebuild the layout just because we fail
// a reference check, so we iterate over the items and perform reference checks
// on each item instead.
const itemsChanged = <ItemType>(oldItems: Array<ItemType>, newItems: Array<ItemType>) => {
  if (oldItems === newItems) {
    return false;
  }

  if (oldItems.length !== newItems.length) {
    return true;
  }

  return !oldItems.every((item, index) => item === newItems[index]);
};

const mergeNewSizesIntoLayout = <ItemType>(
  layout: Layout<ItemType>,
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  newSizes: WeakMap<ItemType, Size>,
): Layout<ItemType> => {
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  const newLayoutInfo: WeakMap<ItemType, ItemLayoutInfo> = new WeakMap();
  let updatedMeasurements = false;

  // Iterate over all items and check to see if we have new size info. If we do,
  // create a new layout with new sizes.
  layout.items.forEach((item) => {
    const itemLayoutInfo = layout.getItemLayoutInfo(item);
    const newSize = newSizes.get(item);

    // Skip items with no layout info.
    if (!itemLayoutInfo) {
      return;
    }

    if (newSize && !isEqual(itemLayoutInfo.size, newSize)) {
      newLayoutInfo.set(item, {
        ...itemLayoutInfo,
        size: newSize,
      });

      updatedMeasurements = true;
    }
  });

  // If we've made changes to sizes, update this layout and use the new sizes.
  // Otherwise, we can just return this Layout. Callers of
  // getLayoutWithNewSizes can do a reference check to see if the layout info
  // actually changed.
  if (updatedMeasurements) {
    return new Layout(
      layout.items,
      (item) => newLayoutInfo.get(item) || layout.getItemLayoutInfo(item),
    );
  }

  return layout;
};

export default class LayoutRenderer<ItemType> extends React.Component<
  Props<ItemType>,
  State<ItemType>,
> {
  static defaultProps = {
    virtualOverscan: defaultGetVirtualOverscan,
  };

  // Initialize state. Items are deliberately initialized to be empty in the
  // constructor so that getDerivedStateFromProps will generate an updated
  // layout. Even if initialLayout is specified, we want to initialize
  // everything else using getDerivedStateFromProps.
  state: State<ItemType> = {
    items: [],
    scrollContainerSize: this.props.scrollContainerSize,
    scrollPosition: this.props.scrollPosition,
    contentContainerSize: this.props.contentContainerSize,
    layout: this.props.initialLayout || new Layout(),
  };

  shouldComponentUpdate(nextProps: Props<ItemType>, nextState: State<ItemType>) {
    const { virtualize } = nextProps;
    const { layout, scrollPosition, scrollContainerSize, contentContainerSize } = this.state;
    const {
      layout: nextLayout,
      scrollPosition: nextScrollPosition,
      scrollContainerSize: nextScrollContainerSize,
      contentContainerSize: nextContentContainerSize,
    } = nextState;

    // Re-render if the layout changed.
    if (nextLayout !== layout) {
      return true;
    }

    // Re-render if the the content container changed size.
    if (!isEqual(nextContentContainerSize, contentContainerSize)) {
      return true;
    }

    // Re-render if the scroll container changed.
    if (!isEqual(nextScrollContainerSize, scrollContainerSize)) {
      return true;
    }

    // Re-render if the scroll position changed.
    if (virtualize && !isEqual(nextScrollPosition, scrollPosition)) {
      return true;
    }

    return false;
  }

  // We're receiving new props which could be as simple as new items and as
  // complicated as morphing this Masonry grid into an entirely new grid with
  // different layout engine or scroll container. We need to figure out which
  // scenario is happening here and update state accordingly.
  static getDerivedStateFromProps(props: Props<ItemType>, state: State<ItemType>) {
    const {
      items,
      getItemLayoutOptions,
      layoutEngine,
      scrollContainerSize,
      scrollPosition,
      contentContainerSize,
    } = props;
    const { items: oldItems, contentContainerSize: oldContentContainerSize, layout } = state;

    // Copy managed props to state so we can compare in future calls to
    // getDerivedStateFromProps.
    const updatedState: $Shape<State<ItemType>> = {
      scrollContainerSize,
      scrollPosition,
      items,
    };

    // If the items array or container size changed, generate a new layout.
    if (itemsChanged(oldItems, items) || !isEqual(contentContainerSize, oldContentContainerSize)) {
      // $FlowFixMe: new errors found from flow 0.96 upgrade
      const newLayout = layoutEngine.buildLayout({
        containerSize: contentContainerSize,
        items: removeNulls(items),
        getItemLayoutOptions,
        previousLayout: layout,
      });

      return {
        layout: newLayout,
        contentContainerSize,
        ...updatedState,
      };
    }

    return updatedState;
  }

  componentWillUnmount() {
    if (this.handleItemsRenderedAnimationFrame) {
      cancelAnimationFrame(this.handleItemsRenderedAnimationFrame);
    }
  }

  // Provide external access to the layout.
  getLayout(): Layout<ItemType> {
    return this.state.layout;
  }

  handleItemsRenderedAnimationFrame: ?AnimationFrameID;

  // The contentContainer element is important to keep around so we can measure
  // its offset inside of the scrollContainer at any time.
  // TODO: We have to manually specify this type until React 16.3 flow types are
  // updated. See: https://github.com/facebook/react/issues/12553
  contentContainerRef: { current: null | HTMLDivElement } = React.createRef();

  // After items are rendered for the first time and are measured, the layout
  // needs to be updated with the size information. This allows variable-sized
  // content to be measured and laid out. handleItemsRendered does this size
  // reconciling, merging new sizes into the current layout.
  handleItemsRendered: (itemSizes: WeakMap<ItemType, Size>) => void = (itemSizes): void => {
    if (!this.handleItemsRenderedAnimationFrame) {
      this.handleItemsRenderedAnimationFrame = requestAnimationFrame(() => {
        this.handleItemsRenderedAnimationFrame = null;

        const { getItemLayoutOptions, layoutEngine, onLayoutUpdated } = this.props;
        const { layout, items, contentContainerSize } = this.state;

        // Merge the item sizes into the layout. If none of the sizes change, the
        // same layout obj is returned.
        const updatedSizesLayout: Layout<ItemType> = mergeNewSizesIntoLayout(layout, itemSizes);

        // If the layout the sizes of the layout match what was rendered, we know
        // we've finally measured everything and the layout is stable. We can then
        // notify our parent via onLayoutUpdated. However, if the sizes did change
        // (and thus updatedSizesLayout is different than the previous layout), we
        // need to re-render a new layout with the updated sizes.
        if (layout === updatedSizesLayout) {
          if (onLayoutUpdated) {
            onLayoutUpdated(layout);
          }
        } else {
          const newLayout = layoutEngine.buildLayout({
            containerSize: contentContainerSize,
            items: removeNulls(items),
            getItemLayoutOptions,
            previousLayout: updatedSizesLayout,
          });

          this.setState({ layout: newLayout });
        }
      });
    }
  };

  renderCollection: (layout: Layout<ItemType>, customSize?: Size) => React.Node = (
    layout: Layout<ItemType>,
    customSize?: Size,
  ) => {
    const {
      renderItem,
      itemsToMeasureLimit,
      scrollContainerSize,
      contentContainerSize,
    } = this.props;

    const itemsToMeasureLimitVal =
      typeof itemsToMeasureLimit === 'function'
        ? itemsToMeasureLimit({
            layout,
            scrollContainerSize,
            contentContainerSize,
          })
        : itemsToMeasureLimit || Infinity;

    return (
      <Collection
        renderItem={renderItem}
        layout={layout}
        onRenderedItems={this.handleItemsRendered}
        customSize={customSize}
        itemsToMeasureLimit={itemsToMeasureLimitVal}
      />
    );
  };

  // Render content based on items and layout. It's assumed at this point that
  // we already have measured containerSize and containerOffset and we're ready
  // to render the current layout in state.
  render() {
    const { virtualize, virtualOverscan } = this.props;
    const { layout, scrollPosition, scrollContainerSize, contentContainerSize } = this.state;

    let content = null;

    // Only virtualize if a scroll container was provided and measured.
    if (virtualize && scrollContainerSize && scrollPosition) {
      const virtualOverscanVal =
        typeof virtualOverscan === 'function'
          ? virtualOverscan({
              layout,
              scrollContainerSize,
              contentContainerSize,
            })
          : virtualOverscan || 0;

      content = (
        <Virtualizer
          layout={layout}
          scrollContainerSize={scrollContainerSize}
          scrollPosition={scrollPosition}
          overscan={virtualOverscanVal}
        >
          {this.renderCollection}
        </Virtualizer>
      );
    } else {
      content = this.renderCollection(layout);
    }

    return <div className="LayoutRenderer">{content}</div>;
  }
}
